import { ListMenuItem, newToast } from "@compass/core";
import { LkPersonTypeId, LkRoleId, Permission, PermissionAction, TenantInfo } from "generated/openApi";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { RootState } from "redux/store";
import { isAuthorized } from "services";
import { StudentBulkUpdatePageState, studentBulkUpdatePageRoute } from "../StudentBulkUpdatePage/StudentBulkUpdatePage";
import {
  StudentBulkAssignGroupPageState,
  studentBulkAssignGroupPageRoute,
} from "../StudentBulkAssignGroupPage/StudentBulkAssignGroupPage";
import {
  StudentBulkCreateServicesPageState,
  studentBulkCreateServicesPageRoute,
} from "../StudentBulkCreateServicesPage/StudentBulkCreateServicesPage";
import { studentBulkEmailPageRoute, StudentBulkEmailPageState } from "../StudentBulkEmailPage/StudentBulkEmailPage";
import { studentBulkMergePageRoute, StudentBulkMergePageState } from "../StudentBulkMergePage/StudentBulkMergePage";
import {
  createTextingConversationsPageRoute,
  CreateTextingConversationsPageState,
} from "pages/texting/CreateTextingConversationsPage/CreateTextingConversationsPage";
import ApiService from "services/apiService";

const MAX_BULK_WALLET_PASS_RECIPIENTS = 10000;
const MAX_BADGES_PER_REQUEST = 5000;

async function getBadgeErrorMessage(error: unknown): Promise<string> {
  const fallback = "Failed to download Mobile Passes.";
  try {
    const errorData = (error as { response?: unknown })?.response;
    const responseText = errorData instanceof Blob
      ? await errorData.text()
      : typeof errorData === "string" ? errorData : "";
    if (!responseText.trim()) return fallback;

    let serverMessage: unknown = responseText;
    try {
      const parsed = JSON.parse(responseText);
      serverMessage = typeof parsed === "string" ? parsed : parsed?.detail;
    } catch {
      // Plain-text responses already contain the server message.
    }
    return typeof serverMessage === "string" && serverMessage.trim() ? serverMessage : fallback;
  } catch {
    return fallback;
  }
}

export interface WalletPassResults {
  sent: number;
  total: number;
  errorMessages: string[];
}

export const useStudentListMenuItems = (props: {
  tenantTextingInfo: TenantInfo | undefined;
  permission: Permission;
  selectedIds: number[];
  clearSelectedIds?: () => void;
  showWalletPassResults?: (results: WalletPassResults) => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const roleId = useSelector((state: RootState) => state.app.roleId);
  const isPrintingRef = useRef(false);
  const menuItems: ListMenuItem[] = [];

  if (isAuthorized(props.permission, PermissionAction.Update))
    menuItems.push({
      label: "Update",
      action: (selectedIds: number[]) =>
        navigate(studentBulkUpdatePageRoute.getPath(), {
          state: { selectedIds: selectedIds } as StudentBulkUpdatePageState,
        }),
    });

  if (isAuthorized(Permission.StudentGroup, PermissionAction.Create))
    menuItems.push({
      label: "Assign Group",
      action: (selectedIds: number[]) =>
        navigate(studentBulkAssignGroupPageRoute.getPath(), {
          state: { selectedIds: selectedIds } as StudentBulkAssignGroupPageState,
        }),
    });

  if (isAuthorized(Permission.StudentService, PermissionAction.Create))
    menuItems.push({
      label: "+ New Services",
      action: (selectedIds: number[]) =>
        navigate(studentBulkCreateServicesPageRoute.getPath(), {
          state: { selectedIds: selectedIds } as StudentBulkCreateServicesPageState,
        }),
    });

  if (isAuthorized(props.permission, PermissionAction.Update))
    menuItems.push({
      label: "Send Emails",
      action: (selectedIds: number[]) =>
        navigate(studentBulkEmailPageRoute.getPath(), {
          state: { selectedIds: selectedIds } as StudentBulkEmailPageState,
        }),
    });

  // Scope this action to the active students table and exclude it from the deleted-students table.
  if (props.clearSelectedIds && isAuthorized(Permission.WalletPass, PermissionAction.Create))
    menuItems.push({
      label: "Email Mobile Passes",
      action: (selectedIds: number[]) => {
        if (selectedIds.length > MAX_BULK_WALLET_PASS_RECIPIENTS) {
          dispatch(
            setAppToast(
              newToast(
                "error",
                `A maximum of ${MAX_BULK_WALLET_PASS_RECIPIENTS} students can be sent Mobile Passes at one time. You have ${selectedIds.length} selected.`
              )
            )
          );
          return;
        }

        if (window.confirm(`Email a Mobile Pass to ${selectedIds.length} selected student(s)?`)) {
          ApiService.walletPass
            .emailBulkWalletPass(selectedIds)
            .then((response) => {
              const errorMessages = response.errorMessages || [];

              if (errorMessages.length === 0) {
                dispatch(
                  setAppToast(newToast("success", `Mobile Passes emailed to ${response.sent} student(s).`))
                );
              } else {
                props.showWalletPassResults?.({
                  sent: response.sent,
                  total: response.total,
                  errorMessages: errorMessages,
                });
                dispatch(
                  setAppToast(
                    newToast(
                      response.sent > 0 ? "warning" : "error",
                      response.sent > 0
                        ? `Sent ${response.sent} of ${response.total}. ${errorMessages.length} failed.`
                        : `No Mobile Passes were sent. ${errorMessages.length} failed.`
                    )
                  )
                );
                console.warn("Wallet Pass email errors:", errorMessages);
              }

              if (response.sent > 0) props.clearSelectedIds?.();
            })
            .catch(() => {
              dispatch(setAppToast(newToast("error", "Failed to email Mobile Passes.")));
            });
        }
      },
    });

  if (props.clearSelectedIds && isAuthorized(props.permission, PermissionAction.View) && roleId !== LkRoleId.DataEntry)
    menuItems.push({
      label: "Print Mobile Passes",
      action: async (selectedIds: number[]) => {
        if (isPrintingRef.current) return;
        if (selectedIds.length > MAX_BADGES_PER_REQUEST) {
          dispatch(setAppToast(newToast("error", `A maximum of ${MAX_BADGES_PER_REQUEST} Mobile Passes can be printed at one time.`)));
          return;
        }

        isPrintingRef.current = true;
        let objectUrl: string | undefined;
        try {
          const summary = await ApiService.students.getBadgeSummary(selectedIds);
          const eligible = summary.eligibleStudents ?? 0;
          const missing = summary.missingIdStudents ?? 0;
          if (eligible === 0) {
            dispatch(setAppToast(newToast("error", "None of the selected students have an ID to print.")));
            return;
          }

          let message = `Print Mobile Passes for ${eligible} student(s)?`;
          if (missing > 0) message += ` ${missing} student(s) without an ID will be skipped.`;
          if (!window.confirm(message)) return;

          dispatch(setAppToast(newToast("info", "Preparing Mobile Passes...")));
          const response = await ApiService.students.downloadQrBadges(selectedIds);
          if (!response?.data) throw new Error("Mobile Pass PDF was not returned.");

          objectUrl = URL.createObjectURL(response.data);
          const link = document.createElement("a");
          link.href = objectUrl;
          link.download = response.fileName || "Mobile_Passes.pdf";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          props.clearSelectedIds?.();
        } catch (error) {
          dispatch(setAppToast(newToast("error", await getBadgeErrorMessage(error))));
        } finally {
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          isPrintingRef.current = false;
        }
      },
    });

  if (isAuthorized(props.permission, PermissionAction.Update)) {
    menuItems.push({
      label: "Merge",
      action: (selectedIds: number[]) =>
        navigate(studentBulkMergePageRoute.getPath(), {
          state: { selectedIds: selectedIds } as StudentBulkMergePageState,
        }),
      disabled: props.selectedIds.length < 2,
    });
  }

  if (props.tenantTextingInfo && props.tenantTextingInfo.canInfobip && isAuthorized(Permission.Texting, PermissionAction.Create)) {
    menuItems.push({
      label: "+ Texting",
      action: (selectedIds: number[]) =>
        navigate(createTextingConversationsPageRoute.getPath(), {
          state: {
            personTypeId: Number(LkPersonTypeId.Student),
            personIds: selectedIds,
          } as CreateTextingConversationsPageState,
        }),
    });
  }

  return menuItems;
};
